import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import './docs';
import { getPrintDoc } from './print-registry';
import { usePrintStore } from './print-store';
import type { PrintSize } from './print-types';
import './print.css';

const PAGE_RULE: Record<PrintSize, string> = {
  '80mm': '@page { size: 80mm auto; margin: 4mm }',
  a4: '@page { size: A4; margin: 15mm }',
};

const nextFrame = (): Promise<void> =>
  new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => { resolve(); });
    else setTimeout(resolve, 0);
  });

/**
 * The print layer. Portalled to <body> so it sits OUTSIDE #root, which print.css
 * hides. Signals ready only after the document has rendered, fonts have loaded
 * and the paint has settled; children effects (barcodes) run before this one.
 */
export function PrintHost(): ReactNode {
  const job = usePrintStore((s) => s.job);
  // The job whose document has said it is ready; ready is only signalled once it matches the current job.
  const [docReadyJob, setDocReadyJob] = useState<unknown>(null);
  const onDocReady = useCallback(() => { setDocReadyJob(job); }, [job]);

  // Scopes print.css's hide-everything rule to an active print job, so Ctrl+P elsewhere prints the screen.
  useEffect(() => {
    if (job === null) return;
    document.body.classList.add('printing');
    return () => {
      document.body.classList.remove('printing');
    };
  }, [job]);

  useEffect(() => {
    if (job === null || docReadyJob !== job) return;
    const run = { cancelled: false };
    void (async () => {
      try {
        if ('fonts' in document) await document.fonts.ready;
      } catch {
        // a font failure must not block printing
      }
      await nextFrame();
      await nextFrame();
      if (!run.cancelled) usePrintStore.getState().markReady();
    })();
    return () => {
      run.cancelled = true;
    };
  }, [job, docReadyJob]);

  if (job === null) return null;
  const Doc = getPrintDoc(job.doc);
  if (Doc === undefined) return null;
  return createPortal(
    <div className="print-root" data-print-size={job.size}>
      <style>{PAGE_RULE[job.size]}</style>
      <Doc key={job.id} data={job.data} size={job.size} onReady={onDocReady} />
    </div>,
    document.body,
  );
}
